/**
 * Freigabeliste der Zahlungsziel-Optionen je Lieferant. Hier werden Zahlungsvorlagen nur AUSGEWAEHLT
 * (Freigaben und Standard); Einsehen, Bearbeiten und Loeschen passiert auf der Seite
 * "Zahlungsvorlagen" - jede Freigabezeile verlinkt direkt auf genau ihre Vorlage
 * (`/zahlungsvorlagen/<id>`). Angelegt (und sofort freigegeben) wird weiterhin hier ueber
 * "Neue Vorlage…". Der Name einer Vorlage ist "[Kommentar] - [Titel aus den Raten]"
 * (`lib/zahlungsvorlagenTitel.ts`).
 */
import { useEffect, useRef, useState, type JSX } from "react";
import { Ikone } from "../Ikone.tsx";
import { NUR_LESEN_TITEL } from "../Drawer/drawerRechte.ts";
import { ladeFreigaben, freigabenErsetzen, type FreigabeZeile } from "../../api/lieferantenZahlungsziele.ts";
import { fehlertext } from "../../lib/fehlertext.ts";
import type { PaymentTemplateRow } from "../../api/zahlungsvorlagen.ts";
import { NeueVorlageDialog } from "./NeueVorlageDialog.tsx";
import { Verweis } from "../Verweis.tsx";
import { vorlagenAnsicht } from "../PaymentTemplates/ZahlungsvorlagenSeite.tsx";
import { vorlagenName } from "../../lib/zahlungsvorlagenTitel.ts";

export interface ZahlungszieleFeldProps {
  kLieferant: number;
  darfSchreiben: boolean;
  vorlagen: readonly PaymentTemplateRow[];
  onFreigabenGespeichert?: (kLieferant: number, freigaben: FreigabeZeile[]) => void;
  onVorlageAngelegt?: (vorlage: PaymentTemplateRow) => void;
}
interface Zeile { templateId: string; isDefault: boolean; }

function zeilenAusServer(freigaben: readonly FreigabeZeile[]): Zeile[] {
  return freigaben.map((z) => ({ templateId: z.templateId, isDefault: z.isDefault }));
}

export function ZahlungszieleFeld({ kLieferant, darfSchreiben, vorlagen, onFreigabenGespeichert, onVorlageAngelegt }: ZahlungszieleFeldProps): JSX.Element {
  const sperrTitel = darfSchreiben ? undefined : NUR_LESEN_TITEL;
  const stand = useRef(0);
  const [zeilen, setZeilen] = useState<Zeile[]>([]);
  const [fehler, setFehler] = useState<string | null>(null);
  const [dialogOffen, setDialogOffen] = useState(false);
  const [dialogSpeichert, setDialogSpeichert] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [ausstehendeFreigabe, setAusstehendeFreigabe] = useState<Zeile[] | null>(null);
  const [neueVorlagen, setNeueVorlagen] = useState<PaymentTemplateRow[]>([]);

  useEffect(() => {
    let verworfen = false;
    const standBeimLaden = stand.current;
    void ladeFreigaben(kLieferant).then(
      (geladen) => {
        if (!verworfen && standBeimLaden === stand.current) setZeilen(zeilenAusServer(geladen));
      },
      (grund: unknown) => {
        if (!verworfen && standBeimLaden === stand.current) setFehler(fehlertext(grund, "Die Freigaben konnten nicht geladen werden."));
      },
    );
    return () => { verworfen = true; };
  }, [kLieferant]);

  async function speichern(neu: Zeile[]): Promise<boolean> {
    if (speichert) return false;
    setFehler(null);
    setSpeichert(true);
    try {
      const gespeichert = await freigabenErsetzen(kLieferant, neu);
      stand.current += 1;
      setZeilen(zeilenAusServer(gespeichert));
      setAusstehendeFreigabe(null);
      onFreigabenGespeichert?.(kLieferant, gespeichert);
      return true;
    } catch (grund) {
      setFehler(fehlertext(grund, "Die Freigaben konnten nicht gespeichert werden."));
      return false;
    } finally {
      setSpeichert(false);
    }
  }

  function ohneZeile(index: number): Zeile[] {
    const rest = zeilen.filter((_, i) => i !== index);
    return rest.map((zeile, i) => ({ ...zeile, isDefault: zeile.isDefault || (i === 0 && !rest.some((z) => z.isDefault)) }));
  }
  function aendereVorlage(index: number, templateId: string): void {
    const neu = zeilen.map((zeile, i) => i === index ? { ...zeile, templateId } : zeile);
    if (neu.some((zeile) => zeile.templateId === "")) setZeilen(neu);
    else void speichern(neu);
  }
  function setzeStandard(index: number): void { void speichern(zeilen.map((zeile, i) => ({ ...zeile, isDefault: i === index }))); }

  async function uebernehmeNeueVorlage(vorlage: PaymentTemplateRow): Promise<void> {
    const leer = zeilen.findIndex((zeile) => zeile.templateId === "");
    const neu = leer === -1
      ? [...zeilen, { templateId: vorlage.id, isDefault: zeilen.length === 0 }]
      : zeilen.map((zeile, i) => i === leer ? { ...zeile, templateId: vorlage.id } : zeile);
    setNeueVorlagen((alt) => alt.some((bekannt) => bekannt.id === vorlage.id) ? alt : [...alt, vorlage]);
    onVorlageAngelegt?.(vorlage);
    setZeilen(neu);
    setAusstehendeFreigabe(neu);
    await speichern(neu);
    setDialogOffen(false);
  }

  const alleVorlagen = [...vorlagen, ...neueVorlagen.filter((neu) => !vorlagen.some((vorlage) => vorlage.id === neu.id))];
  const freieVorlagen = (eigene: string) => alleVorlagen.filter((v) => v.id === eigene || !zeilen.some((z) => z.templateId === v.id));
  const gesperrt = !darfSchreiben || speichert || dialogSpeichert || ausstehendeFreigabe !== null;

  return <div className="plan-feld" role="group" aria-label="Freigegebene Zahlungsziele">
    <table className="plan-tabelle freigaben-tabelle"><thead><tr><th scope="col">Zahlungsziel</th><th scope="col">Standard</th><th scope="col"></th><th scope="col"></th></tr></thead><tbody>
      {zeilen.map((zeile, index) => <tr key={`${zeile.templateId}-${index}`}>
        <td><select aria-label={`Zahlungsziel der Option ${index + 1}`} value={zeile.templateId} disabled={gesperrt} title={sperrTitel} onChange={(e) => aendereVorlage(index, e.target.value)}><option value="">Zahlungsziel wählen…</option>{freieVorlagen(zeile.templateId).map((vorlage) => <option key={vorlage.id} value={vorlage.id}>{(Array.isArray(vorlage.rows) && vorlage.rows.length > 0 && vorlage.rows.every((r) => "anchor" in r) ? vorlagenName(vorlage.comment, vorlage.rows) : vorlage.label)}</option>)}</select></td>
        <td><input type="radio" name={`standard-${kLieferant}`} aria-label={`Option ${index + 1} als Standard`} checked={zeile.isDefault} disabled={gesperrt} title={sperrTitel} onChange={() => setzeStandard(index)} /></td>
        <td>{zeile.templateId !== "" ? <Verweis ziel={vorlagenAnsicht(zeile.templateId)} className="plan-vorlage-link"><Ikone name="external-link" /> Vorlage ansehen/bearbeiten</Verweis> : null}</td>
        <td><button type="button" className="plan-zeile-weg" aria-label={`Option ${index + 1} entfernen`} disabled={gesperrt} title={sperrTitel} onClick={() => void speichern(ohneZeile(index))}><Ikone name="x" /></button></td>
      </tr>)}
    </tbody></table>
    <p className="plan-vorlagen-verwalten"><Verweis ziel={vorlagenAnsicht(null)}>Alle Zahlungsvorlagen verwalten →</Verweis></p>
    {fehler ? <p className="plan-fehler" role="alert">{fehler}</p> : null}
    {ausstehendeFreigabe ? <button type="button" className="btn-sec" disabled={!darfSchreiben || speichert} title={sperrTitel} onClick={() => { if (darfSchreiben) void speichern(ausstehendeFreigabe); }}>Freigabe erneut versuchen</button> : null}
    <div className="plan-fuss"><button type="button" className="plan-zeile-neu" disabled={gesperrt} title={sperrTitel} onClick={() => setZeilen([...zeilen, { templateId: "", isDefault: zeilen.length === 0 }])}><Ikone name="circle-plus" /> Option hinzufügen</button><button type="button" className="btn-sec" disabled={gesperrt} title={sperrTitel} onClick={() => setDialogOffen(true)}><Ikone name="circle-plus" /> Neue Vorlage…</button></div>
    {dialogOffen ? <NeueVorlageDialog onSchliessen={() => setDialogOffen(false)} onLaufstatusAendern={setDialogSpeichert} onAngelegt={uebernehmeNeueVorlage} /> : null}
  </div>;
}
